/**
 * ItemPreviewPanel - Side panel for previewing menu item details
 * 
 * Story 1.7: Side-by-side persistent panel (NOT overlay)
 * 
 * Features:
 * - Hidden by default, appears when user clicks View or row
 * - Occupies 33% of width on desktop (table becomes 66%)
 * - Sits alongside table on same z-axis level (NOT overlay)
 * - Table remains fully interactive (users can select multiple rows)
 * - Selecting new rows updates preview WITHOUT closing panel
 * - Closes ONLY via: Close button (×) or ESC key
 * - Back button hidden in iframe preview (via hideBack URL param)
 * - Loading and error states
 * - Responsive design with flexbox
 * - Full-height iframe display without scrolling
 */

/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';

// Buffer pixels added to iframe height to ensure full content is visible
const IFRAME_HEIGHT_BUFFER = 50;

const ItemPreviewPanel = ({ isOpen, itemUrl, itemName, onClose, isModalContext = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const closeButtonRef = useRef(null);
  const iframeRef = useRef(null);
  const contentRef = useRef(null);

  /**
   * Reset scroll positions for both container and iframe content
   * Handles cross-origin access errors gracefully
   */
  const resetScrollPositions = () => {
    // Reset the container scroll position to top
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }

    // Reset iframe content scroll position if accessible
    if (iframeRef.current) {
      try {
        const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
        const iframeWindow = iframeRef.current.contentWindow;
        
        if (iframeDoc && iframeWindow) {
          iframeWindow.scrollTo(0, 0);
          iframeDoc.documentElement.scrollTop = 0;
          iframeDoc.body.scrollTop = 0;
        }
      } catch (error) {
        // Cross-origin or access denied - ignore
        // The container scroll reset above is sufficient
      }
    }
  };

  /**
   * Handle ESC key to close panel
   */
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  /**
   * Focus close button when panel opens
   */
  useEffect(() => {
    if (isOpen && closeButtonRef.current) {
      closeButtonRef.current.focus();
    }
  }, [isOpen]);

  /**
   * Reset loading state when URL changes
   */
  useEffect(() => {
    if (itemUrl) {
      setIsLoading(true);
      setLoadError(false);
    }
  }, [itemUrl]);

  /**
   * Reset scroll position when itemUrl changes (switching to another menu item)
   */
  useEffect(() => {
    if (itemUrl) {
      resetScrollPositions();
    }
  }, [itemUrl]);

  /**
   * Handle iframe load completion
   * Story 1.7: Dynamically resize iframe to show full content in table context
   */
  const handleIframeLoad = () => {
    setIsLoading(false);
    setLoadError(false);

    // Reset scroll position when iframe loads (ensures it's at top for new content)
    resetScrollPositions();

    // Only resize in table context (not modal context)
    if (!isModalContext && iframeRef.current) {
      try {
        // Access iframe's content document (same-origin)
        const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
        
        if (iframeDoc?.body) {
          // Get the full scroll height of the iframe content
          const contentHeight = Math.max(
            iframeDoc.body.scrollHeight,
            iframeDoc.documentElement.scrollHeight
          );
          
          // Set iframe height to match content height (add small buffer)
          iframeRef.current.style.height = `${contentHeight + IFRAME_HEIGHT_BUFFER}px`;
          iframeRef.current.style.minHeight = `${contentHeight + IFRAME_HEIGHT_BUFFER}px`;
        }
      } catch (error) {
        // Cross-origin or access denied - fallback to large min-height
        console.warn('Unable to access iframe content for auto-sizing:', error);
      }
    }
  };

  /**
   * Handle iframe load error
   */
  const handleIframeError = () => {
    setIsLoading(false);
    setLoadError(true);
  };

  // Don't render the container if panel is not open
  // The CSS display: none/flex handles visibility via .ls-validation-content-container--panel-open class
  if (!isOpen) return null;

  return (
    <aside 
      className={`ls-item-preview-panel ${isModalContext ? 'ls-item-preview-panel--modal-context' : ''}`}
      aria-label="Item Preview Panel"
    >
      {/* Panel Header */}
      <div className="ls-item-preview-header">
        <h3 className="ls-item-preview-title">{itemName || 'Item Details'}</h3>
        <button
          ref={closeButtonRef}
          className="ls-item-preview-close"
          onClick={onClose}
          aria-label="Close preview panel"
          type="button"
        >
          ×
        </button>
      </div>

      {/* Panel Content */}
      <div ref={contentRef} className="ls-item-preview-content">
        {/* Loading Indicator */}
        {isLoading && (
          <div className="ls-item-preview-loader">
            <div className="ls-item-preview-spinner" />
            <p>Loading item details...</p>
          </div>
        )}

        {/* Error Message */}
        {loadError && (
          <div className="ls-item-preview-error">
            <p>Failed to load item details.</p>
          </div>
        )}

        {/* Iframe - Item Detail Page - CRITICAL: Full height without scrollbar */}
        {!loadError && itemUrl && (
          <iframe
            ref={iframeRef}
            src={itemUrl}
            title={`Preview: ${itemName || 'Item'}`}
            className="ls-item-preview-iframe"
            sandbox="allow-scripts allow-same-origin allow-forms"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            style={{ overflow: isModalContext ? 'auto' : 'hidden' }}
          />
        )}
      </div>
    </aside>
  );
};

export default ItemPreviewPanel;

